import { FC, useRef, useState } from 'react';
import Webcam from 'react-webcam';

import MaterialIcon from '../../../../components/MaterialIcon/MaterialIcon';
import styles from './PhotoPicker.module.scss';
import Thumbnails from './Thumbnails';
import { PhotoPickerProps, usePhotoPicker } from './usePhotoPicker';

const dataUrlToFile = (dataUrl: string, filename: string): File => {
  const [meta, base64] = dataUrl.split(',');
  const mime = meta.match(/data:(.*?);/)?.[1] ?? 'image/jpeg';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new File([bytes], filename, { type: mime });
};

const AndroidPhotoPicker: FC<PhotoPickerProps> = (props) => {
  const { t, processingCount, errorMessage, previews, isFull, processFiles, handleFiles, handleRemove } =
    usePhotoPicker(props);
  const libraryInputRef = useRef<HTMLInputElement>(null);
  const webcamRef = useRef<Webcam>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraUnavailable, setCameraUnavailable] = useState(false);

  const handleTakePhotoClick = () => {
    setCameraActive(true);
  };

  const handleLibraryClick = () => {
    libraryInputRef.current?.click();
  };

  const handleCloseCamera = () => {
    setCameraActive(false);
    setCameraReady(false);
  };

  const handleCapture = () => {
    const screenshot = webcamRef.current?.getScreenshot();

    if (screenshot) {
      processFiles([dataUrlToFile(screenshot, `photo-${Date.now()}.jpg`)]);
    }

    handleCloseCamera();
  };

  const handleUserMedia = () => {
    setCameraReady(true);
    setCameraUnavailable(false);
  };

  const handleUserError = () => {
    setCameraReady(false);
    setCameraUnavailable(true);
  };

  const showTakePhoto = !cameraUnavailable;

  return (
    <div className={styles.picker}>
      {cameraActive && !cameraUnavailable && (
        <div className={styles.cameraModal}>
          <Webcam
            audio={false}
            className={styles.cameraModalVideo}
            onUserMedia={handleUserMedia}
            onUserMediaError={handleUserError}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            videoConstraints={{ facingMode: 'environment' }}
          />
          <button
            aria-label={t('meterkast:closeCamera')}
            className={styles.cameraModalClose}
            onClick={handleCloseCamera}
            type="button"
          >
            <MaterialIcon name="close" size={28} />
          </button>
          <div className={styles.cameraModalBar}>
            <button
              aria-label={t('meterkast:takePhoto')}
              className={styles.cameraModalShutter}
              disabled={!cameraReady}
              onClick={handleCapture}
              type="button"
            />
          </div>
        </div>
      )}

      {cameraUnavailable && (
        <p className={styles.notice}>{t('meterkast:cameraUnavailable')}</p>
      )}

      <div className={styles.grid}>
        <Thumbnails
          onRemove={handleRemove}
          previews={previews}
          processingCount={processingCount}
          removeLabel={t('meterkast:removePhoto')}
        />
      </div>

      {!isFull && (
        <div className={styles.actions}>
          {showTakePhoto && (
            <button
              className={styles.actionBtn}
              onClick={handleTakePhotoClick}
              type="button"
            >
              <MaterialIcon name="photo_camera" size={24} />
              <span>{t('meterkast:takePhoto')}</span>
            </button>
          )}
          <button
            className={styles.actionBtn}
            onClick={handleLibraryClick}
            type="button"
          >
            <MaterialIcon name="photo_library" size={24} />
            <span>{t('meterkast:chooseFromLibrary')}</span>
          </button>
        </div>
      )}

      {errorMessage && <p className={styles.error}>{errorMessage}</p>}

      <input
        accept="image/*"
        className={styles.hiddenInput}
        data-test-id="photo-input"
        multiple
        onChange={handleFiles}
        ref={libraryInputRef}
        type="file"
      />
    </div>
  );
};

export default AndroidPhotoPicker;
